import { Text } from "@otrium/atoms";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import { locale } from "src/config/app-config";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { Carousel } from "src/organisms/Carousel";
import { theme } from "src/theme";
import type {
  BrandCarousel,
  Edit,
  EditCarousel,
  SummaryBrand,
} from "src/types/graphql.d";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { ResultHeader } from "./ResultHeader";
import { CarouselType } from "src/types/Carousels.d";

type DefaultResultProps = {
  data: BrandCarousel | EditCarousel;
  onViewAllClick?: () => void;
  onRecommendationClick?: (data: {
    type: CarouselType;
    name: string;
    position: number;
  }) => void;
};

function isSummaryBrand(data: SummaryBrand | Edit): data is SummaryBrand {
  return data.__typename === "SummaryBrand";
}

function DefaultResult({
  data,
  onViewAllClick,
  onRecommendationClick,
}: DefaultResultProps) {
  const { currentShopType } = useShopTypeContext();

  const items = useMemo(() => {
    const brandsRoute = `${getI18nRoute({ route: `/brands`, locale })}/`;
    const genderRoute =
      currentShopType !== ShopType.All
        ? `${getI18nRoute({
            route: `/${currentShopType}`,
            locale,
          })}`
        : "";

    return data.items.map((item, idx) => (
      <div
        className="default-result-item"
        key={item.id}
        // Constructor index sections by zero-state carousel type:
        //   Popular brands carousel → "Brands"
        //   Shop-by-category / edit carousel → "Categories"
        // (Typed-query autocomplete rows live in SuggestionItem with
        // "Search Suggestions"; product cards live under ProductCard with
        // "Products" via the cnstrcRecommendation path.)
        data-cnstrc-item-section={
          isSummaryBrand(item) ? "Brands" : "Categories"
        }
        data-cnstrc-item-id={item.id}
        data-cnstrc-item-name={item.name}
      >
        <Link
          href={
            isSummaryBrand(item)
              ? `${brandsRoute}${item.slug}${genderRoute}`
              : item.url
          }
          prefetch={false}
          onClick={
            onRecommendationClick
              ? () =>
                  onRecommendationClick({
                    type: isSummaryBrand(item)
                      ? CarouselType.Brand
                      : CarouselType.Edit,
                    name: item.name,
                    position: idx + 1,
                  })
              : undefined
          }
        >
          {isSummaryBrand(item) ? (
            <Image src={item.logo} alt={item.name} width={100} height={60} />
          ) : (
            <>
              <Image src={item.image} alt={item.name} width={100} height={60} />
              <Text as="span" fontSize={1} fontWeight={600} textAlign="center">
                {item.name}
              </Text>
            </>
          )}
        </Link>
      </div>
    ));
  }, [data.items, currentShopType, onRecommendationClick]);

  if (!data.items.length) {
    return null;
  }

  return (
    <div className="default-result-wrapper">
      <ResultHeader
        title={data.title}
        viewAllLink={data.view_all_url || undefined}
        onViewAllClick={onViewAllClick}
      />
      <Carousel
        items={items}
        parentPadding={theme.space.space16}
        fullWidthMobile
        itemsPerView={{
          mobile: 2.75,
          tablet: 2.75,
        }}
      />
    </div>
  );
}

export { DefaultResult };
